import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { EzLogo } from "@/pages/LandingPage";
import { FileSearch, Briefcase, Phone, MessageCircle, Target, Sparkles, ClipboardCheck, Quote, Star, Bot } from "lucide-react";
import mixpanel from "@/mixpanel";
import { api } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SALES_ANALYSIS_STEPS = [
  { text: "Reviewing your application...", icon: FileSearch, duration: 3000 },
  { text: "AI is analyzing your resume...", icon: Briefcase, duration: 4000 },
  { text: "Evaluating sales experience...", icon: Phone, duration: 4000 },
  { text: "Assessing communication skills...", icon: MessageCircle, duration: 4000 },
  { text: "Matching qualifications to role...", icon: Target, duration: 4000 },
  { text: "Finalizing AI review...", icon: Sparkles, duration: 5000 },
];

const MIN_DISPLAY_TIME = 8000;

// Total runtime of the loader animation; scoring almost always finishes within
// this window, so we keep a steady cadence here and only back off afterward.
const ANALYSIS_DURATION = SALES_ANALYSIS_STEPS.reduce((sum, s) => sum + s.duration, 0);

// Score-status polling: poll every 3s while the loader animation is playing,
// then exponentially back off to a 15s ceiling for unusually slow scores.
const POLL_INITIAL_MS = 3000;
const POLL_MAX_MS = 15000;
const POLL_BACKOFF_FACTOR = 1.5;

function SalesAnalysisLoader({ appId, onComplete }) {
  const [progress, setProgress] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);
  const startTime = useRef(Date.now());
  const pollRef = useRef(null);

  useEffect(() => { mixpanel.track("applicationReviewStarted", { job_type: "sales" }); }, []);

  useEffect(() => {
    let elapsed = 0;
    const timeouts = SALES_ANALYSIS_STEPS.map((step, i) => {
      elapsed += step.duration;
      return setTimeout(() => setStepIndex(i), elapsed - step.duration);
    });
    return () => timeouts.forEach(clearTimeout);
  }, []);

  // Poll for score completion, then stop (a complete result never changes).
  // Steady 3s cadence while the animation plays, backing off to 15s after.
  useEffect(() => {
    if (!appId) return;
    let cancelled = false;
    let delay = POLL_INITIAL_MS;
    const poll = async () => {
      try {
        const res = await api.get(`${API}/applications/${appId}/score-status`);
        const { status } = res.data;
        if (status === "complete" && !cancelled) {
          const elapsed = Date.now() - startTime.current;
          const remaining = Math.max(0, MIN_DISPLAY_TIME - elapsed);
          setTimeout(() => {
            if (!cancelled) {
              setProgress(100);
              setTimeout(() => onComplete(), 600);
            }
          }, remaining);
          return;
        }
      } catch {}
      if (cancelled) return;
      const elapsed = Date.now() - startTime.current;
      delay = elapsed < ANALYSIS_DURATION
        ? POLL_INITIAL_MS
        : Math.min(delay * POLL_BACKOFF_FACTOR, POLL_MAX_MS);
      pollRef.current = setTimeout(poll, delay);
    };
    pollRef.current = setTimeout(poll, delay);
    return () => { cancelled = true; clearTimeout(pollRef.current); };
  }, [appId, onComplete]);

  useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime.current;
      if (progress >= 100) { clearInterval(interval); return; }
      const t = Math.min(elapsed / 25000, 1);
      const eased = 90 * (1 - Math.pow(1 - t, 3));
      setProgress(Math.min(eased, 90));
    }, 100);
    return () => clearInterval(interval);
  }, [progress]);

  const StepIcon = SALES_ANALYSIS_STEPS[stepIndex]?.icon || FileSearch;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <EzLogo size="h-7" />
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-lg w-full text-center">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-ez-blue/10 to-ez-teal/10 flex items-center justify-center mx-auto mb-6 border border-ez-blue/20">
            <ClipboardCheck className="w-10 h-10 text-ez-teal" />
          </div>
          <h2 className="text-2xl font-bold text-ez-teal font-heading mb-2">
            Reviewing Your Application
          </h2>
          <div className="flex items-center justify-center gap-2 mb-8">
            <Bot className="w-4 h-4 text-slate-400" />
            <p className="text-sm text-slate-400 font-body">Our AI is quickly reviewing your profile</p>
          </div>

          <div className="relative w-full mb-4">
            <div className="w-full bg-slate-100 rounded-full h-5 overflow-hidden shadow-inner">
              <motion.div
                className="h-full rounded-full relative overflow-hidden"
                style={{
                  width: `${Math.max(progress, 2)}%`,
                  background: "linear-gradient(90deg, #3b82f6, #0d9488, #14b8a6)",
                }}
                transition={{ duration: 0.1 }}
              >
                <div className="absolute inset-0 opacity-30" style={{
                  backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(255,255,255,0.3) 8px, rgba(255,255,255,0.3) 16px)",
                  animation: "stripe-move 0.8s linear infinite",
                }} />
              </motion.div>
            </div>
            <p className="text-xs font-semibold text-ez-teal mt-2 font-heading">{Math.round(progress)}%</p>
          </div>

          <div className="flex items-center justify-center gap-3 mb-10">
            <AnimatePresence mode="wait">
              <motion.div
                key={stepIndex}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3 }}
                className="w-8 h-8 rounded-full bg-ez-blue/10 flex items-center justify-center flex-shrink-0"
              >
                <StepIcon className="w-4 h-4 text-ez-blue" />
              </motion.div>
            </AnimatePresence>
            <AnimatePresence mode="wait">
              <motion.p
                key={stepIndex}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
                className="text-slate-500 font-body text-sm"
              >
                {SALES_ANALYSIS_STEPS[stepIndex]?.text}
              </motion.p>
            </AnimatePresence>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.5 }}
            className="bg-ez-surface rounded-2xl border border-slate-100 p-5 text-left shadow-sm"
          >
            <Quote className="w-5 h-5 text-ez-peach/40 mb-2" />
            <p className="text-slate-600 font-body italic text-sm leading-relaxed mb-3">
              "I love this job because of the flexibility and I'm able to be present in my kids' lives working from home. I have a great boss who's understanding and listens to my concerns. I finally feel like I found my home where I'm being heard and appreciated. The money is fabulous as well which is huge for me! I love the fact that I'm helping seniors stay home and independent and making a difference in the quality of their lives."
            </p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                  <span className="text-emerald-700 font-bold text-xs font-heading">A</span>
                </div>
                <div>
                  <p className="font-semibold text-ez-teal text-xs font-heading">Amy D.</p>
                  <p className="text-[11px] text-slate-500 font-body">Sales Concierge, Ez Health</p>
                </div>
              </div>
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-ez-peach text-ez-peach" />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <style>{`
        @keyframes stripe-move {
          0% { background-position: 0 0; }
          100% { background-position: 32px 0; }
        }
      `}</style>
    </div>
  );
}

export default function SalesWilloRedirect() {
  const location = useLocation();
  const navigate = useNavigate();

  const appId = location.state?.appId;
  const scoring = location.state?.scoring;

  useEffect(() => {
    if (!appId && !scoring) {
      navigate("/sales/thank-you", { replace: true });
    }
  }, [appId, scoring, navigate]);

  // Sales never gets an immediate Willo step. Regardless of score, everyone sees
  // the review loader and then lands on the thank-you page. Qualifying candidates
  // are advanced to the Willo stage in Ashby later by auto_ashby_loop (on the
  // score-based delay) — that's when their Willo invite goes out — so we never
  // surface a Willo link or a "check your email" screen here.
  const handleScoringComplete = useCallback(() => {
    navigate("/sales/thank-you", { replace: true });
  }, [navigate]);

  return <SalesAnalysisLoader appId={appId} onComplete={handleScoringComplete} />;
}
